import React, { useEffect, useState } from 'react';
import { Image as ImageIcon, Maximize, Download, X } from 'react-feather';
import { Typography } from '@/UIlibrary/Typography/Typography';
import { useSupabaseStore } from './supabaseStore';
import { SUPABASE_BUCKET } from './types';

interface ImageContentProps {
	url: string;
}

const ImageContent: React.FC<ImageContentProps> = ({ url }) => {
	const [error, setError] = useState<string | null>(null);
	const [signedUrl, setSignedUrl] = useState<string | null>(null);
	const [imageLoaded, setImageLoaded] = useState(false);
	const [fullscreen, setFullscreen] = useState(false);

	const supabase = useSupabaseStore((state) => state.supabase);

	useEffect(() => {
		const getSignedUrl = async () => {
			try {
				const pathMatch = url.match(`/${SUPABASE_BUCKET.WHATSAPP_IMAGES}/(.*)`);
				if (!pathMatch) {
					throw new Error('Invalid storage URL format');
				}

				const path = pathMatch[1];
				const { data, error } = await supabase.storage
					.from(SUPABASE_BUCKET.WHATSAPP_IMAGES)
					.createSignedUrl(path, 3600);

				if (error) throw error;
				setSignedUrl(data.signedUrl);
			} catch (err) {
				console.error('Error getting signed URL:', err);
				setError('Failed to get image access');
				setImageLoaded(false);
			}
		};

		if (url) {
			getSignedUrl();
		}
	}, [url, supabase.storage]);

	const handleImageLoad = () => setImageLoaded(true);
	const handleImageError = () => setError('Failed to load image');

	const toggleFullscreen = () => {
		setFullscreen(!fullscreen);
		if (!fullscreen) {
			document.body.style.overflow = 'hidden';
		} else {
			document.body.style.overflow = '';
		}
	};

	const handleDownload = async () => {
		if (!signedUrl) return;

		try {
			const fileName = url.split('/').pop() || 'downloaded-image.jpg';

			const response = await fetch(signedUrl);
			const blob = await response.blob();

			const downloadUrl = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = downloadUrl;
			link.download = fileName;
			document.body.appendChild(link);
			link.click();

			window.URL.revokeObjectURL(downloadUrl);
			document.body.removeChild(link);
		} catch (err) {
			console.error('Error downloading image:', err);
			setError('Failed to download image');
		}
	};

	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape' && fullscreen) {
				toggleFullscreen();
			}
		};

		window.addEventListener('keydown', handleKeyDown);
		return () => {
			window.removeEventListener('keydown', handleKeyDown);
			document.body.style.overflow = '';
		};
	}, [fullscreen]);

	return (
		<>
			<div className="relative min-h-[200px] min-w-[200px] bg-gray-50 rounded-lg overflow-hidden group">
				{/* Loading skeleton */}
				<div
					className={`absolute inset-0 flex items-center justify-center 
          transition-opacity duration-300 ${imageLoaded ? 'opacity-0' : 'opacity-100'}`}
				>
					<div className="animate-pulse flex flex-col items-center">
						<ImageIcon className="w-8 h-8 text-gray-300 mb-2" />
						<Typography size="sm" color="subtitle">
							...{' '}
						</Typography>
					</div>
				</div>

				{/* Image */}
				{signedUrl && (
					<div className="relative">
						<img
							src={signedUrl}
							alt="Message attachment"
							className={`max-w-full rounded-lg transition-opacity duration-300 cursor-pointer
                ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
							style={{
								maxHeight: '300px',
								objectFit: 'contain',
								width: '100%',
								height: '100%'
							}}
							onLoad={handleImageLoad}
							onError={handleImageError}
							onClick={toggleFullscreen}
						/>

						<div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex gap-2">
							<button
								onClick={(e) => {
									e.stopPropagation();
									toggleFullscreen();
								}}
								className="bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition-all duration-200"
								title="View fullscreen"
							>
								<Maximize size={16} />
							</button>
							<button
								onClick={(e) => {
									e.stopPropagation();
									handleDownload();
								}}
								className="bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition-all duration-200"
								title="Download image"
							>
								<Download size={16} />
							</button>
						</div>
					</div>
				)}

				{error && (
					<div className="absolute inset-0 flex items-center justify-center">
						<div className="text-red-500 text-sm bg-white p-2 rounded shadow">{error}</div>
					</div>
				)}
			</div>

			{fullscreen && signedUrl && (
				<div className="fixed inset-0 z-50 bg-black bg-opacity-90 flex flex-col items-center justify-center">
					<div className="absolute top-4 right-4 flex gap-4">
						<button
							onClick={handleDownload}
							className="bg-black bg-opacity-50 text-white p-3 rounded-full hover:bg-opacity-70 transition-all duration-200"
							title="Download image"
						>
							<Download size={20} />
						</button>
						<button
							onClick={toggleFullscreen}
							className="bg-black bg-opacity-50 text-white p-3 rounded-full hover:bg-opacity-70 transition-all duration-200"
							title="Close fullscreen"
						>
							<X size={20} />
						</button>
					</div>

					<div className="w-full h-full p-8 flex items-center justify-center">
						<img
							src={signedUrl}
							alt="Message attachment (fullscreen)"
							className="max-h-full max-w-full object-contain"
							onClick={(e) => e.stopPropagation()}
						/>
					</div>
				</div>
			)}
		</>
	);
};

export default ImageContent;
